/**
 * O interruptor da prévia de link do chat (27/10/2026).
 *
 * `GET /chat/configuracoes` é aberto a qualquer autenticado — o chat precisa saber se pede o
 * cartão do link ANTES de a pessoa colar um. `PUT` exige `chat_config:manage` (papel
 * `Administrator`); a tela Administração › Chat é a chamadora.
 *
 * Nomes em PT no contrato (`previa_de_link_ligada`), como a resposta vem do backend — API BR ×
 * front EN. `atualizado_por_nome` já vem pronto do servidor (sem objeto de usuário).
 *
 * Caminho SEM `/api/v1`: o `baseURL` do `api` (`@/services/api`) já termina em `/api/v1`
 * (`VITE_API_URL`); um segundo prefixo aqui duplicaria e o backend responderia 404 — cicatriz
 * travada por `useConfiguracoesDoChat.url.test.tsx`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/services/api";

export interface ConfiguracoesDoChat {
  previa_de_link_ligada: boolean;
  atualizado_por_nome: string | null;
  updated_at: string | null;
}

/** Chave compartilhada entre a leitura e a gravação — uma mutação bem-sucedida atualiza o cache
 * direto, sem depender de um refetch para a tela refletir. */
export const CHAVE_CONFIGURACOES_DO_CHAT = ["chat", "configuracoes"] as const;

/**
 * `refetchInterval` de 60 s (com a aba à vista: o React Query não relê em segundo plano), `staleTime` de 60 s e
 * `refetchOnWindowFocus`: o administrador desliga (ou religa) o interruptor e a conversa que já estava aberta
 * acompanha em até 1 minuto, sem recarregar a página e sem trocar de aba. Cada cartão na tela monta este hook, mas
 * o React Query reinicia os intervalos de todos os observadores juntos a cada atualização da query: é uma leitura
 * por minuto por aba, não uma por cartão (travado em `useConfiguracoesDoChat.atualizacao.test.tsx`).
 *
 * `enabled: false` existe para o portal: o parceiro não tem sessão interna e este GET daria 401
 * (e o 401 do `api` apaga a sessão interna). Quem monta o hook sem sessão interna passa `false`.
 */
export function useConfiguracoesDoChat(opcoes?: { enabled?: boolean }) {
  const enabled = opcoes?.enabled ?? true;
  return useQuery<ConfiguracoesDoChat>({
    queryKey: CHAVE_CONFIGURACOES_DO_CHAT,
    queryFn: async () => (await api.get("/chat/configuracoes")).data,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: enabled ? 60_000 : false,
    enabled,
  });
}

export function useSalvarConfiguracoesDoChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ligada: boolean): Promise<ConfiguracoesDoChat> =>
      (await api.put("/chat/configuracoes", { previa_de_link_ligada: ligada })).data,
    onSuccess: (dados) => {
      qc.setQueryData(CHAVE_CONFIGURACOES_DO_CHAT, dados);
    },
  });
}
